import WandbLoader from '@wandb/weave/common/components/WandbLoader';
import {Button} from '@wandb/weave/components';
import * as DropdownMenu from '@wandb/weave/components/DropdownMenu';
import {Icon} from '@wandb/weave/components/Icon';
import * as Panel2 from '@wandb/weave/components/Panel2/panel';
import {
  constFunction,
  constNone,
  constString,
  isAssignableTo,
  Node,
  opArtifactAliasAlias,
  opArtifactId,
  opArtifactIsPortfolio,
  opArtifactLastMembership,
  opArtifactMembershipArtifactAliases,
  opArtifactMembershipArtifactVersion,
  opArtifactMembershipForAlias,
  opArtifactMemberships,
  opArtifactMembershipVersionIndex,
  opArtifactName,
  opArtifactProject,
  opArtifactType,
  opArtifactTypeName,
  opArtifactVersionId,
  opArtifactVersions,
  opCount,
  opDict,
  opEntityName,
  opMap,
  opProjectEntity,
  opProjectInternalId,
  opProjectName,
  OutputNode,
} from '@wandb/weave/core';
import {
  useNodeValue,
  useNodeValueExecutor,
  useRefreshAllNodes,
} from '@wandb/weave/react';
import * as React from 'react';
import {useContext, useRef, useState} from 'react';
import {useHistory} from 'react-router-dom';
import {toast} from 'react-toastify';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Dropdown} from 'semantic-ui-react';

import {envIsPublicCloud} from '../../../../config';
import {
  useMoveArtifactCollectionMutation,
  useUpdateArtifactCollectionMutation,
  useUpdateArtifactPortfolioMutation,
} from '../../../../generated/graphql';
import {useHandleScroll} from '../../../../pages/HomePage/HomePageSidebar/useHandleScroll';
import {DEFAULT_ONPREM_ORGANIZATION_NAME} from '../../../../pages/OrgDashboard/OrgRoles/RolesDashboard';
import {Analytics} from '../../../../services/analytics';
import {
  PremiumFeatureTooltip,
  useIsAutomationEnabled,
} from '../../../../util/accounts/pricing';
import {isVersionAlias} from '../../../../util/artifacts';
import {propagateErrorsContext} from '../../../../util/errors';
import {trackCreateTriggerClicked} from '../../../../util/navigation';
import * as urls from '../../../../util/urls';
import ArtifactActionHistory from '../../../ArtifactActionHistory';
import {CreateTriggerDrawerForCollection} from '../../../ArtifactCollectionSettings/CreateTriggerModal';
import {
  DeleteArtifactModal,
  MoveArtifactModal,
  RenameArtifactModal,
} from '../../../ArtifactSidebar/ArtifactSidebarActions';
import {useIsProjectReadOnly} from '../../../Registries/common/hooks';
import {
  checkRegistryProject,
  REGISTRY_PROJECT_PREFIX,
  resolveRegistryUrl,
} from '../../../Registries/common/utils';
import {AddArtifactDrawer} from '../../../Registries/workflows/CreateRegisteredCollectionDrawer/AddArtifactDrawer/AddArtifactDrawer';
import {AccountSelectorContext} from '../../../Search/SearchNav/AccountSelectorContextProvider';
import {
  ConfigType as PanelArtifactMembershipConfigType,
  PanelArtifactMembership,
} from '../../artifactMembership/PanelArtifactMembership';
import {PanelArtifactMembershipOverviewCollectionSection} from '../../artifactMembership/PanelArtifactMembershipOverview/PanelArtifactMembershipOverviewCollectionSection';
import {TriggersOverviewTable} from '../../artifactMembership/PanelArtifactMembershipOverview/TriggersOverviewTable';
import {
  ConfigType as PanelArtifactHistoryConfigType,
  PanelArtifactHistory,
} from '../PanelArtifactHistory';
import {PanelEmptyArtifact} from '../PanelEmptyArtifact';
// eslint-disable-next-line import/no-cycle -- please fix if you can
import {
  ArtifactCollectionViewType,
  isArtifactCollectionView,
} from './ArtifactPanelRoot';
import * as S from './style';

const inputType = 'artifact' as const;

type selectedCollectionViewType = 'membership' | ArtifactCollectionViewType;

export type ConfigType = {
  // selectedCollectionView determines the collection-level view selected - null should default to membership
  selectedCollectionView?: selectedCollectionViewType;
  tabConfigs?: {
    overview?: PanelArtifactMembershipConfigType; // for legacy reasons, `overview` is used for `membership` config
    versions?: PanelArtifactHistoryConfigType;
  };
  // The following field is only applicable when selectedCollectionView is 'membership'
  // selectedMembershipIdentifier determines the membership identifier selected - null should default to latest membership version
  selectedMembershipIdentifier?: string;
};

const PanelArtifact: React.FC<
  Panel2.PanelProps<typeof inputType, ConfigType> & {artifactRegistry?: boolean}
> = props => {
  // 1. Extract Props
  const {updateConfig, config} = props;
  const artifactCollectionNode = props.input;
  const collectionView = config?.selectedCollectionView;
  const membershipIdentifier = config?.selectedMembershipIdentifier;

  // 2. Component State
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [collectionRenameModalOpen, setCollectionRenameModalOpen] =
    React.useState(false);
  const [collectionDeleteModalOpen, setCollectionDeleteModalOpen] =
    React.useState(false);
  const [isMovingCollection, setMovingCollection] = useState(false);
  const [addTriggerModalOpen, setAddTriggerModalOpen] = React.useState(false);
  const [isLinkArtifactModalOpen, setIsLinkArtifactModalOpen] = useState(false);

  // 3. Initialize Child Configs
  const tabConfig = Panel2.useConfigChild('tabConfigs', config, updateConfig);
  const membershipConfig = Panel2.useConfigChild(
    'overview',
    tabConfig.config,
    tabConfig.updateConfig
  );
  const versionsConfig = Panel2.useConfigChild(
    'versions',
    tabConfig.config,
    tabConfig.updateConfig
  );

  // 4. General Hooks
  const refreshAll = useRefreshAllNodes();
  const cgExecutor = useNodeValueExecutor();
  const ref = useRef<HTMLDivElement | null>(null);
  // TODO: This should be removed - we don't want URL management done
  // in panels - very bad
  const history = useHistory();
  const [updateArtifactPortfolio] = useUpdateArtifactPortfolioMutation({
    context: propagateErrorsContext(),
  });
  const [updateArtifactCollection] = useUpdateArtifactCollectionMutation({
    context: propagateErrorsContext(),
  });
  const [moveArtifactCollection] = useMoveArtifactCollectionMutation({
    context: propagateErrorsContext(),
  });

  // 6. Panel Business Logic
  // Get the collection data needed for this component via Weave Query
  const artifactCollectionData = useNodeValue(
    React.useMemo(
      () => dataDictNode(artifactCollectionNode),
      [artifactCollectionNode]
    )
  );

  const isPortfolio = artifactCollectionData.result?.collectionIsPortfolio;
  const {isProjectReadOnly: readOnly, loading: isProjectReadOnlyLoading} =
    useIsProjectReadOnly(
      artifactCollectionData.result?.entityName ?? '',
      artifactCollectionData.result?.projectName ?? ''
    );

  // 5. Define User Actions
  const setSelectedMembershipIdentifier = React.useCallback(
    (newIdentifier: string) => {
      updateConfig({
        selectedMembershipIdentifier: newIdentifier,
        selectedCollectionView: 'membership',
        // When selecting a membership, we reset to the overview subtab
        tabConfigs: {
          ...config?.tabConfigs,
          overview: {
            ...config?.tabConfigs?.overview,
            selectedTab: 'overview',
          },
        },
      });
      if (artifactCollectionData.result != null) {
        Analytics.track('Artifact interaction', {
          entityName: artifactCollectionData.result.entityName,
          projectName: artifactCollectionData.result.projectName,
          artifactCollectionName: artifactCollectionData.result.collectionName,
          artifactType: artifactCollectionData.result.artifactTypeName,
          artifactCollectionView: 'membership',
          artifactCollectionMembership: newIdentifier,
          artifactCollectionType: isPortfolio ? 'portfolio' : 'sequence',
        });
      }
    },
    [updateConfig, config, artifactCollectionData.result, isPortfolio]
  );

  const setSelectedCollectionView = React.useCallback(
    (newView: ArtifactCollectionViewType) => {
      updateConfig({
        selectedMembershipIdentifier: undefined,
        selectedCollectionView: newView,
        tabConfigs: {
          ...config?.tabConfigs,
          overview: {
            ...config?.tabConfigs?.overview,
            selectedTab: undefined,
          },
        },
      });
      if (artifactCollectionData.result != null) {
        Analytics.track('Artifact interaction', {
          entityName: artifactCollectionData.result.entityName,
          projectName: artifactCollectionData.result.projectName,
          artifactCollectionName: artifactCollectionData.result.collectionName,
          artifactType: artifactCollectionData.result.artifactTypeName,
          artifactCollectionView: newView,
          artifactCollectionType: isPortfolio ? 'portfolio' : 'sequence',
        });
      }
    },
    [updateConfig, config, isPortfolio, artifactCollectionData.result]
  );

  const onHistorySelect = React.useCallback(
    (index: number, node: Node) => {
      if (isAssignableTo(node.type, 'artifactMembership')) {
        cgExecutor(
          opArtifactMembershipVersionIndex({artifactMembership: node})
        ).then(versionIndex => {
          if (versionIndex != null) {
            setSelectedMembershipIdentifier('v' + versionIndex);
          }
        });
      }
    },
    [cgExecutor, setSelectedMembershipIdentifier]
  );

  // Get the Weave Membership Node
  const artifactMembershipNode = React.useMemo(() => {
    if (membershipIdentifier == null) {
      return constNone();
    }
    return opArtifactMembershipForAlias({
      artifact: artifactCollectionNode,
      aliasName: constString(membershipIdentifier),
    }) as OutputNode<'artifactMembership'>;
  }, [artifactCollectionNode, membershipIdentifier]);

  // Get the Weave Data Node for the membership version
  const currentVersionDictNode = React.useMemo(() => {
    return opDict({
      index: opArtifactMembershipVersionIndex({
        artifactMembership: artifactMembershipNode,
      }),
      id: opArtifactVersionId({
        artifactVersion: opArtifactMembershipArtifactVersion({
          artifactMembership: artifactMembershipNode,
        }),
      }),
    } as any) as OutputNode<{
      type: 'typedDict';
      propertyTypes: {
        index: 'number';
        id: 'string';
      };
    }>;
  }, [artifactMembershipNode]);

  // Get the current version data needed for this component
  const currentVersionValue = useNodeValue(currentVersionDictNode);

  // Extract some derived properties
  const lastVersionIndex =
    artifactCollectionData.result?.lastMembershipVersionIndex;
  const selectedMembershipMissing =
    membershipIdentifier != null &&
    !currentVersionValue.loading &&
    currentVersionValue.result?.id === null;
  const collectionIsEmpty =
    !artifactCollectionData.loading && lastVersionIndex == null;

  // 7. Effects
  // Here we want to update the selected membership when viewing the membership tab, under 2 cases:
  // a. No identifier is selected, but there is a default identifier (at least one member).
  // b. An identifier is selected, but it is not present in the collection
  React.useEffect(() => {
    // Default view when no view (collection or membership-level) is given to us from the config or URL
    if (collectionView == null && membershipIdentifier == null) {
      setSelectedCollectionView('versions');
    }
    // If we are looking at the membership tab, and there exists at least one member
    else if (collectionView === 'membership' && !collectionIsEmpty) {
      if (
        membershipIdentifier == null &&
        membershipIdentifier !== `v${lastVersionIndex}`
      ) {
        // If there is no selected membership, or the selected membership is missing,
        // switch to the latest membership
        setSelectedMembershipIdentifier('v' + lastVersionIndex);
      } else if (
        selectedMembershipMissing &&
        membershipIdentifier !== `v${lastVersionIndex}`
      ) {
        setSelectedMembershipIdentifier('v' + lastVersionIndex);
      } else if (selectedMembershipMissing) {
        // If the currentVersionValue is null, that means the viewer doesn't have permission to view it,
        // therefore switch to the versions view
        toast("You don't have permission to view the version details");
        setSelectedCollectionView('versions');
      }
    } else if (collectionView === 'membership' && collectionIsEmpty) {
      setSelectedCollectionView('versions');
    }
    if (
      // If the selected collection view is not of ArtifactCollectionViewType ('versions', 'membership'
      // or 'action_history'), update the config view to 'versions'
      !['versions', 'membership', 'action_history', undefined].includes(
        collectionView
      )
    ) {
      setSelectedCollectionView('versions');
    }
  }, [
    collectionView,
    lastVersionIndex,
    membershipIdentifier,
    selectedMembershipMissing,
    setSelectedMembershipIdentifier,
    setSelectedCollectionView,
    currentVersionValue.result?.id,
    currentVersionValue.loading,
    collectionIsEmpty,
    updateConfig,
  ]);

  // 8. Child Props
  const dropdownOptions = React.useMemo(() => {
    if (artifactCollectionData.result == null) {
      return [];
    }
    let options: Array<{
      value?: string;
      text?: string;
      header?: boolean;
      headerContent?: string;
      divider?: boolean;
    }> = [{value: 'versions', text: 'All Versions'}];
    if (isPortfolio) {
      options.push({value: 'action_history', text: 'Action History'});
    }
    const alias2version: {[key: string]: string} = {};
    const versions =
      artifactCollectionData.result.collectionMemberships?.map(m => {
        const v = `v${m.versionIndex}`;
        m.aliases.filter(a => a !== v).forEach(a => (alias2version[a] = v));
        return {
          value: v,
          text: v,
        };
      }) ?? [];

    const aliasOptions = Object.keys(alias2version).map(a => {
      return {value: `${a}`, text: a};
    });

    options = [
      ...options,
      {header: true, headerContent: 'Aliases'},
      {divider: true},
      ...aliasOptions,
      {header: true, headerContent: 'Versions'},
      {divider: true},
      ...versions,
    ];

    return options;
  }, [artifactCollectionData.result, isPortfolio]);

  const dropdownText = React.useMemo(() => {
    if (currentVersionValue.loading) {
      return '';
    }
    if (collectionView === 'action_history') {
      return 'Action History';
    } else if (collectionView === 'versions') {
      return 'Versions';
    } else if (isVersionAlias(membershipIdentifier)) {
      return `Version ${currentVersionValue.result.index}`;
    } else {
      return membershipIdentifier;
    }
  }, [currentVersionValue, collectionView, membershipIdentifier]);

  const scrolled = useHandleScroll();

  const {selectedAccount} = useContext(AccountSelectorContext);
  const hasAutomationAccess = useIsAutomationEnabled(selectedAccount);
  const accountOrgName = envIsPublicCloud
    ? selectedAccount?.name ?? ''
    : DEFAULT_ONPREM_ORGANIZATION_NAME;

  if (artifactCollectionData.loading || isProjectReadOnlyLoading) {
    return <WandbLoader name="artifact-collection" />;
  }
  if (currentVersionValue.loading) {
    return (
      <WandbLoader name="artifact-collection-membership" inline size="tiny" />
    );
  }

  const artifactCollectionDataValue = artifactCollectionData.result!;

  if (
    artifactCollectionDataValue.collectionName == null ||
    artifactCollectionDataValue.projectName == null
  ) {
    return <div>Error: Could not load</div>;
  }
  const collectionHeaderComp = (
    <S.PanelArtifactHeaderContent>
      <S.PanelArtifactHeaderTitle>
        {artifactCollectionDataValue.collectionName}
        <S.CollectionDropdown
          data-test="artifact-collection-dropdown"
          button
          compact
          scrolling
          text={dropdownText}
          // Set to default modal z-index. This dropdown overlays a lot of things
          // so z-index needs to be high
          style={{zIndex: '1001'}}>
          <Dropdown.Menu>
            {dropdownOptions.map((option, i) => {
              if (option.header === true) {
                return (
                  <Dropdown.Header
                    key={`header-${i}`}
                    content={option.headerContent}
                  />
                );
              } else if (option.divider === true) {
                return <Dropdown.Divider key={`divider-${i}`} />;
              } else {
                return (
                  <Dropdown.Item
                    key={`item-${i}`}
                    text={option.text}
                    value={option.value}
                    onClick={(d, {value}) => {
                      const v = value as string;
                      if (isArtifactCollectionView(v)) {
                        setSelectedCollectionView(v);
                      } else {
                        setSelectedMembershipIdentifier('' + v);
                      }
                    }}
                  />
                );
              }
            })}
          </Dropdown.Menu>
        </S.CollectionDropdown>
      </S.PanelArtifactHeaderTitle>
      {!readOnly && (
        <S.TriggerContainer className={'overview-option-ellipses'}>
          <DropdownMenu.Root open={isMenuOpen} onOpenChange={setIsMenuOpen}>
            <DropdownMenu.Trigger>
              <Button
                size="medium"
                variant="ghost"
                icon="overflow-horizontal"
                active={isMenuOpen}
              />
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content align="end">
                <DropdownMenu.Group>
                  <DropdownMenu.Item
                    onClick={() => setCollectionRenameModalOpen(true)}>
                    <Icon name="pencil-edit" />
                    Rename
                  </DropdownMenu.Item>
                  {!isPortfolio && (
                    <DropdownMenu.Item
                      onClick={() => setMovingCollection(true)}>
                      <Icon name="folder-project-move" />
                      Move
                    </DropdownMenu.Item>
                  )}
                  {props.artifactRegistry && (
                    <DropdownMenu.Item
                      disabled={!hasAutomationAccess}
                      onClick={() => {
                        setAddTriggerModalOpen(true);
                        trackCreateTriggerClicked(
                          'Model registry collection overflow menu'
                        );
                      }}>
                      <Icon name="trigger-alt" />
                      <>
                        Create automation
                        <PremiumFeatureTooltip
                          isPremiumFeatureDisabled={!hasAutomationAccess}
                        />
                      </>
                    </DropdownMenu.Item>
                  )}
                  {props.artifactRegistry && (
                    <DropdownMenu.Item
                      onClick={() => setIsLinkArtifactModalOpen(true)}>
                      <Icon name="add-new" />
                      Link new version
                    </DropdownMenu.Item>
                  )}
                </DropdownMenu.Group>

                {isPortfolio && (
                  <>
                    <DropdownMenu.Separator />
                    <DropdownMenu.Item
                      onClick={() => setCollectionDeleteModalOpen(true)}>
                      <Icon name="delete" />
                      Delete
                    </DropdownMenu.Item>
                  </>
                )}
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        </S.TriggerContainer>
      )}
      <RenameArtifactModal
        name={artifactCollectionDataValue.collectionName}
        modalOpen={collectionRenameModalOpen}
        onRenameArtifact={async newName => {
          try {
            if (isPortfolio) {
              await updateArtifactPortfolio({
                variables: {
                  artifactPortfolioID: artifactCollectionDataValue.collectionId,
                  name: newName,
                },
              });
            } else {
              await updateArtifactCollection({
                variables: {
                  artifactSequenceID: artifactCollectionDataValue.collectionId,
                  name: newName,
                },
              });
            }
          } catch (error) {
            console.error(error);
            toast('Something went wrong while trying to rename this artifact.');
          }
          setCollectionRenameModalOpen(false);
          await refreshAll();
        }}
        onClose={async () => {
          setCollectionRenameModalOpen(false);
        }}
      />
      <MoveArtifactModal
        entityName={artifactCollectionDataValue.entityName}
        projectName={artifactCollectionDataValue.projectName}
        modalOpen={isMovingCollection}
        onMoveArtifact={async destinationArtifactTypeName => {
          await moveArtifactCollection({
            variables: {
              artifactSequenceID: artifactCollectionDataValue.collectionId,
              destinationArtifactTypeName,
            },
          });
          setMovingCollection(false);
          await refreshAll();
          history.push(
            urls.artifactSequence({
              entityName: artifactCollectionDataValue.entityName,
              projectName: artifactCollectionDataValue.projectName,
              artifactTypeName: destinationArtifactTypeName,
              artifactSequenceName: artifactCollectionDataValue.collectionName,
            })
          );
        }}
        onClose={() => setMovingCollection(false)}
      />
      <DeleteArtifactModal
        id={artifactCollectionDataValue.collectionId}
        entityName={artifactCollectionDataValue.entityName}
        projectName={artifactCollectionDataValue.projectName}
        artifactTypeName={artifactCollectionDataValue.artifactTypeName}
        artifactCollectionName={artifactCollectionDataValue.collectionName}
        isPortfolio={isPortfolio}
        isModalOpen={collectionDeleteModalOpen}
        refetchProjectArtifacts={async () => {
          const {registryName, isRegistryProject} = checkRegistryProject(
            artifactCollectionDataValue.projectName
          );
          await refreshAll();
          let url = urls.artifactSequence({
            entityName: artifactCollectionDataValue.entityName,
            projectName: artifactCollectionDataValue.projectName,
            artifactTypeName: artifactCollectionDataValue.artifactTypeName,
            artifactSequenceName: artifactCollectionDataValue.collectionName,
          });
          if (isPortfolio) {
            url = resolveRegistryUrl(
              isRegistryProject,
              accountOrgName,
              registryName ?? 'model',
              undefined,
              artifactCollectionDataValue.entityName,
              'versions',
              undefined,
              'overview'
            );
          }
          history.push(url);
        }}
        setIsDeleteModalOpen={setCollectionDeleteModalOpen}
      />
      {addTriggerModalOpen && (
        <CreateTriggerDrawerForCollection
          open={addTriggerModalOpen}
          onClose={() => {
            setAddTriggerModalOpen(false);
          }}
          projectName={artifactCollectionDataValue.projectName}
          entityName={artifactCollectionDataValue.entityName}
          projectID={artifactCollectionDataValue.projectId}
          artifactCollectionID={artifactCollectionDataValue.collectionId}
          artifactCollectionName={artifactCollectionDataValue.collectionName}
          scrollWidth={scrolled.scrollWidth}
        />
      )}
      <AddArtifactDrawer
        open={isLinkArtifactModalOpen}
        closeDrawer={() => setIsLinkArtifactModalOpen(false)}
        defaultProjectName={
          artifactCollectionDataValue.projectName.startsWith(
            REGISTRY_PROJECT_PREFIX
          )
            ? artifactCollectionDataValue.projectName
            : `${artifactCollectionDataValue.artifactTypeName}-registry`
        }
        collection={{
          ...artifactCollectionDataValue,
          name: artifactCollectionDataValue.collectionName,
          id: artifactCollectionDataValue.collectionId,
          numVersions: artifactCollectionDataValue.versionCount,
          description: '',
          tags: [],
        }}
        scrollWidth={scrolled.scrollWidth}
      />
    </S.PanelArtifactHeaderContent>
  );
  const collectionIsEmptyInRegistry =
    !artifactCollectionData.loading &&
    lastVersionIndex == null &&
    props.artifactRegistry;

  if (collectionIsEmpty && !props.artifactRegistry) {
    return (
      <S.ArtifactCollectionMain>
        <S.BlankPageWrapper>
          <PanelEmptyArtifact
            {...Panel2.dummyProps}
            input={props.input}
            isPortfolio={isPortfolio}
          />
        </S.BlankPageWrapper>
      </S.ArtifactCollectionMain>
    );
  }

  return (
    <S.ArtifactCollectionMain>
      {collectionHeaderComp}
      {collectionView === 'action_history' && (
        <S.PanelWrapper>
          <ArtifactActionHistory
            entityName={artifactCollectionDataValue.entityName}
            projectName={artifactCollectionDataValue.projectName}
            artifactTypeName={artifactCollectionDataValue.artifactTypeName}
            artifactCollectionName={artifactCollectionDataValue.collectionName}
            pageSize={10}
          />
        </S.PanelWrapper>
      )}
      {collectionView === 'versions' && (
        <S.PanelWrapper>
          <S.OverviewPanel>
            <PanelArtifactMembershipOverviewCollectionSection
              {...Panel2.dummyProps}
              readOnly={readOnly}
              artifactRegistry={props.artifactRegistry}
              input={props.input as any}
              inputRef={ref}
            />
          </S.OverviewPanel>
          {!collectionIsEmpty && (
            <S.OverviewPanel>
              <S.VersionsPanelSectionHeader>
                <S.VersionsPanelSectionHeaderTitle>
                  {'Versions'}
                </S.VersionsPanelSectionHeaderTitle>
              </S.VersionsPanelSectionHeader>
              <PanelArtifactHistory
                input={props.input}
                config={versionsConfig.config}
                updateConfig={versionsConfig.updateConfig}
                context={props.context}
                updateContext={props.updateContext}
                onRowClick={onHistorySelect}
                artifactRegistry={props.artifactRegistry}
              />
            </S.OverviewPanel>
          )}
          {props.artifactRegistry && (
            <S.OverviewPanel>
              <TriggersOverviewTable
                entityName={artifactCollectionDataValue.entityName}
                projectName={artifactCollectionDataValue.projectName}
                projectID={artifactCollectionDataValue.projectId}
                collectionID={artifactCollectionDataValue.collectionId}
                collectionName={artifactCollectionDataValue.collectionName}
                inputRef={ref}
              />
            </S.OverviewPanel>
          )}
        </S.PanelWrapper>
      )}
      {!selectedMembershipMissing && membershipIdentifier != null && (
        <PanelArtifactMembership
          input={artifactMembershipNode as any}
          config={membershipConfig.config}
          context={props.context}
          updateContext={props.updateContext}
          updateConfig={membershipConfig.updateConfig}
          targetIdentifier={membershipIdentifier}
          artifactRegistry={props.artifactRegistry}
          artifactTypeName={artifactCollectionDataValue.artifactTypeName}
          entityName={artifactCollectionDataValue.entityName}
          projectName={artifactCollectionDataValue.projectName}
          orgName={accountOrgName}
          artifactCollectionName={artifactCollectionDataValue.collectionName}
          isPortfolio={isPortfolio}
        />
      )}
    </S.ArtifactCollectionMain>
  );
};

export default PanelArtifact;

function dataDictNode(artifactNode: Node<'artifact'>) {
  return opDict({
    collectionName: opArtifactName({artifact: artifactNode}),
    collectionId: opArtifactId({artifact: artifactNode}),
    collectionIsPortfolio: opArtifactIsPortfolio({
      artifact: artifactNode,
    }),
    collectionMemberships: opMap({
      arr: opArtifactMemberships({
        artifact: artifactNode,
      }),
      mapFn: constFunction({row: 'artifactMembership'}, ({row}) => {
        return opDict({
          versionIndex: opArtifactMembershipVersionIndex({
            artifactMembership: row,
          }),
          aliases: opArtifactAliasAlias({
            artifactAlias: opArtifactMembershipArtifactAliases({
              artifactMembership: row,
            }),
          }),
        } as any);
      }),
    }),
    versionCount: opCount({
      arr: opArtifactVersions({artifact: artifactNode}),
    }),
    lastMembershipVersionIndex: opArtifactMembershipVersionIndex({
      artifactMembership: opArtifactLastMembership({
        artifact: artifactNode,
      }),
    }),
    entityName: opEntityName({
      entity: opProjectEntity({
        project: opArtifactProject({
          artifact: artifactNode,
        }),
      }),
    }),
    projectName: opProjectName({
      project: opArtifactProject({
        artifact: artifactNode,
      }),
    }),
    projectId: opProjectInternalId({
      project: opArtifactProject({
        artifact: artifactNode,
      }),
    }),
    artifactTypeName: opArtifactTypeName({
      artifactType: opArtifactType({
        artifact: artifactNode,
      }),
    }),
  } as any) as OutputNode<{
    type: 'typedDict';
    propertyTypes: {
      collectionName: 'string';
      collectionId: 'string';
      collectionIsPortfolio: 'boolean';
      collectionMemberships: {
        type: 'list';
        objectType: {
          type: 'typedDict';
          propertyTypes: {
            versionIndex: 'number';
            aliases: {type: 'list'; objectType: 'string'};
          };
        };
      };
      versionCount: 'number';
      lastMembershipVersionIndex: 'number';
      entityName: 'string';
      projectName: 'string';
      projectId: 'string';
      artifactTypeName: 'string';
    };
  }>;
}
